import { createAsyncThunk } from '@reduxjs/toolkit';
import RootState from '../states/root.state';
import { getHistory } from '../../services/api/spaHistory.api';
import {setSpaHistory, setSpaHistoryProgress} from "../reducers/spaHistory.reducer";

export const fetchSpaHistory = createAsyncThunk<
    any,
    { guid: string; date_from: number;date_to: number,keys: any[], type:string},
    { rejectValue: string; state: RootState }
>(
    'spa-history',
    async (spaInfo, { getState, rejectWithValue, dispatch }) => {
        const { user } = getState();
        const token = user.accessToken;
        let startKey: string | null = null;
        let totalFetched = 0;
        // Runaway backstop only. The requested range is capped at one month (enforced
        // both in the UI and on the backend), so a normal report finishes well under
        // this. We keep paginating until the whole range is fetched instead of cutting
        // off at a row count, which previously dropped the oldest part of the month.
        const MAX_TOTAL_ITEMS = 100000;

        // Buffer every page locally and commit once at the end. Committing per chunk
        // re-rendered (and re-sorted) the whole graph/table on every page, which made
        // long reports feel slow. We report progress as we go and render a single time.
        const buffer: any[] = [];
        const span = Math.max(1, spaInfo.date_to - spaInfo.date_from);
        let oldestSeen = spaInfo.date_to;

        try {
            do {
                const response: any = await getHistory(token, spaInfo, startKey);

                const chunk = response.data.items;

                totalFetched += chunk.length;
                buffer.push(...chunk);

                // The query runs newest-first, so progress = how far back toward
                // date_from the oldest row we've fetched has reached.
                for (const row of chunk) {
                    const ts = row?.timestamp ? new Date(row.timestamp).getTime() : oldestSeen;
                    if (ts < oldestSeen) oldestSeen = ts;
                }
                const pct = Math.min(99, Math.max(0, Math.round(((spaInfo.date_to - oldestSeen) / span) * 100)));
                dispatch(setSpaHistoryProgress(pct));

                startKey = response.data.last_evaluated_key;

                if (totalFetched >= MAX_TOTAL_ITEMS) {
                    console.warn(`Spa history fetch hit the ${MAX_TOTAL_ITEMS}-item runaway backstop; results may be incomplete.`);
                    break;
                }
            }
            while (startKey);

            // Single commit: de-dupes across page boundaries and renders the report once.
            dispatch(setSpaHistory(buffer));

            return true;
        } catch (error: any) {
            if (error.response) {
                return rejectWithValue(error.response.data.data.message??error.response.data.message??"An error occurred while fetching spa history.");
            } else {
                return rejectWithValue(`An error occurred while fetching ${spaInfo.type} history.`);
            }
        }
    }
);